/**
 * Cart toast notification and header count updates (page cart mode).
 */
class CartNotification {
  static STALE_KEY = 'cart-count-stale';

  static getToastElement() {
    return document.getElementById('cart-toast');
  }

  static markCartStale() {
    try {
      sessionStorage.setItem(this.STALE_KEY, '1');
    } catch (error) {
      /* sessionStorage unavailable */
    }
  }

  static clearCartStale() {
    try {
      sessionStorage.removeItem(this.STALE_KEY);
    } catch (error) {
      /* sessionStorage unavailable */
    }
  }

  static isCartStale() {
    try {
      return sessionStorage.getItem(this.STALE_KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  static updateCartCount(itemCount, animate = true) {
    const count = parseInt(itemCount, 10) || 0;

    document.querySelectorAll('.cart-count').forEach((element) => {
      element.textContent = count;
      element.hidden = count <= 0;

      if (count > 0 && animate) {
        element.classList.remove('cart-count--pop');
        void element.offsetWidth;
        element.classList.add('cart-count--pop');
      }
    });
  }

  static animateCartIcon() {
    const cartItem = document.querySelector('.header__utils__item--cart');

    if (!cartItem) return;

    cartItem.classList.remove('cart-icon--bump');
    void cartItem.offsetWidth;
    cartItem.classList.add('cart-icon--bump');

    clearTimeout(this._cartIconTimeout);
    this._cartIconTimeout = setTimeout(() => {
      cartItem.classList.remove('cart-icon--bump');
    }, 700);
  }

  static restartCheckAnimation(toast) {
    const check = toast.querySelector('.cart-toast__check');

    if (!check) return;

    check.classList.remove('is-animating');
    void check.offsetWidth;
    check.classList.add('is-animating');
  }

  static show(message) {
    const toast = this.getToastElement();
    if (!toast) return;

    const text = toast.querySelector('[data-cart-toast-text]');
    if (text) text.textContent = message;

    this.restartCheckAnimation(toast);

    toast.classList.add('is-visible');
    toast.setAttribute('aria-hidden', 'false');

    clearTimeout(this._hideTimeout);
    this._hideTimeout = setTimeout(() => this.hide(), 4500);
  }

  static hide() {
    const toast = this.getToastElement();
    if (!toast) return;

    toast.classList.remove('is-visible');
    toast.setAttribute('aria-hidden', 'true');
  }

  static fetchCart() {
    const url = `${routes.ajax_api}?_=${Date.now()}`;

    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    }).then((response) => response.json());
  }

  static syncCartCountFromServer(animate = false) {
    return this.fetchCart()
      .then((cartData) => {
        this.updateCartCount(cartData.item_count, animate);
        this.clearCartStale();
        return cartData;
      })
      .catch(() => {});
  }

  static refreshCartCountIfNeeded() {
    if (this._syncInFlight) {
      return this._syncInFlight;
    }

    this._syncInFlight = this.syncCartCountFromServer(false).finally(() => {
      this._syncInFlight = null;
    });

    return this._syncInFlight;
  }

  static afterItemAdded(addedItemResponse, cartDrawer = null) {
    this.clearCartStale();

    return this.fetchCart()
      .then((cartData) => {
        this.updateCartCount(cartData.item_count, true);
        this.animateCartIcon();

        if (cartDrawer && typeof cartDrawer.refreshAfterAdd === 'function') {
          cartDrawer.refreshAfterAdd(addedItemResponse, cartData);
        }

        const message = window.theme?.strings?.product?.addedToCart
          || 'Producto añadido al carrito';

        this.show(message);
        return cartData;
      });
  }

  static init() {
    if (this._initialized) return;

    this._initialized = true;

    if (this.isCartStale()) {
      this.refreshCartCountIfNeeded();
    } else {
      this.syncCartCountFromServer(false);
    }

    window.addEventListener('pageshow', () => {
      this.refreshCartCountIfNeeded();
    });

    window.addEventListener('popstate', () => {
      this.refreshCartCountIfNeeded();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      if (this.isCartStale()) {
        this.refreshCartCountIfNeeded();
      }
    });

    window.addEventListener('focus', () => {
      if (!this.isCartStale()) return;

      clearTimeout(this._focusSyncTimeout);
      this._focusSyncTimeout = setTimeout(() => {
        this.refreshCartCountIfNeeded();
      }, 50);
    });
  }
}

window.CartNotification = CartNotification;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CartNotification.init());
} else {
  CartNotification.init();
}
